import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { Textarea } from '@/components/ui/textarea'
import { DecimalInput } from '@/components/ui/DecimalInput'
import type { FamilyEditorSchemaDto } from './types'
import type { Dispatch, SetStateAction } from 'react'

type SchemaField = FamilyEditorSchemaDto['fields'][number]

interface FamilyEditorFieldsSectionProps {
  title: string
  fields: SchemaField[]
  values: Record<string, unknown>
  onChangeValues: Dispatch<SetStateAction<Record<string, unknown>>>
  missingFieldIds?: string[]
  isFieldRequired?: (field: SchemaField) => boolean
}

export function FamilyEditorFieldsSection({
  title,
  fields,
  values,
  onChangeValues,
  missingFieldIds,
  isFieldRequired,
}: FamilyEditorFieldsSectionProps) {
  const isMissing = (fieldId: string) => (missingFieldIds || []).includes(fieldId)
  const isRequired = (field: SchemaField) => (isFieldRequired ? isFieldRequired(field) : false)

  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{title}</div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {fields.map((f) => {
          const rawType = (f.type || '').toLowerCase()
          const configAny = f.config
          const configKind = typeof configAny?.kind === 'string' ? String(configAny.kind).toLowerCase() : undefined
          const configIsText = configKind === 'text'
          const configIsNumber = configKind === 'number'
          const textMinLength = configIsText ? (configAny?.minLength ?? undefined) : undefined
          const textMaxLength = configIsText ? (configAny?.maxLength ?? undefined) : undefined
          const textMultiline = configIsText ? configAny?.multiline === true : false

          const numberUnit = configIsNumber ? (configAny?.unit ?? undefined) : undefined

          const setFieldValue = (next: unknown) => {
            onChangeValues((prev) => ({ ...prev, [f.id]: next }))
          }

          const value = values[f.id]

          const enumOptionsAny = f.enumOptions
          const enumOptionsItems = enumOptionsAny?.items
          const enumOptionsOptions = enumOptionsAny?.options
          const enumItems: Array<{ value: string; label: string }> = Array.isArray(enumOptionsItems)
            ? (enumOptionsItems as unknown[])
                .filter((it): it is { value: string; label?: unknown } => typeof (it as Record<string, unknown>)?.value === 'string')
                .map((it) => ({ value: String(it.value), label: String(it.label ?? it.value) }))
            : Array.isArray(enumOptionsOptions)
              ? (enumOptionsOptions as unknown[])
                  .filter((it): it is { value: string; label?: unknown } => typeof (it as Record<string, unknown>)?.value === 'string')
                  .map((it) => ({ value: String(it.value), label: String(it.label ?? it.value) }))
              : []

          const missing = isMissing(f.id)
          const required = isRequired(f)
          const inputClassName =
            'h-9 ' + (missing ? 'border-amber-400 focus-visible:ring-amber-300' : '')

          return (
            <div key={f.id} className="space-y-1">
              {(rawType === 'bool' || rawType === 'boolean') ? (
                <div className="flex items-center gap-2 h-9">
                  <Checkbox
                    id={`fes-field-${f.id}`}
                    checked={value === true}
                    onCheckedChange={(v) => setFieldValue(v === true)}
                  />
                  <Label htmlFor={`fes-field-${f.id}`} className="text-sm">
                    {f.label}
                    {required ? <span className="text-muted-foreground"> *</span> : null}
                  </Label>
                </div>
              ) : (
                <>
                  <Label className="text-sm">
                    {f.label}
                    {required ? <span className="text-muted-foreground"> *</span> : null}
                  </Label>

                  {rawType === 'text' && (
                    textMultiline ? (
                      <Textarea
                        className={(missing ? 'border-amber-400 focus-visible:ring-amber-300 ' : '')}
                        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
                        minLength={typeof textMinLength === 'number' ? textMinLength : undefined}
                        maxLength={typeof textMaxLength === 'number' ? textMaxLength : undefined}
                        onChange={(e) => setFieldValue(e.target.value)}
                      />
                    ) : (
                      <Input
                        className={inputClassName}
                        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
                        minLength={typeof textMinLength === 'number' ? textMinLength : undefined}
                        maxLength={typeof textMaxLength === 'number' ? textMaxLength : undefined}
                        onChange={(e) => setFieldValue(e.target.value)}
                      />
                    )
                  )}

                  {rawType === 'number' && (
                    <div className="relative">
                      <DecimalInput
                        className={inputClassName + (typeof numberUnit === 'string' && numberUnit.length > 0 ? ' pr-10' : '')}
                        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
                        onValueChange={(next) => {
                          setFieldValue(next === '' ? undefined : next)
                        }}
                      />
                      {typeof numberUnit === 'string' && numberUnit.length > 0 ? (
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {numberUnit}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {rawType === 'enum' && (
                    <Combobox
                      options={enumItems}
                      value={typeof value === 'string' ? value : value == null ? undefined : String(value)}
                      onChange={(v) => setFieldValue(v === '' ? undefined : v)}
                      placeholder="Sélectionner"
                      allowCustomValue
                      className={inputClassName}
                    />
                  )}

                  {missing && required ? (
                    <div className="text-xs text-amber-800">Champ requis.</div>
                  ) : null}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
